import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axiosInstance from 'Services/APIService';

// More flexible interfaces to handle different asset types
export interface SimulationConfig {
  iterations: number;
  years: number;
  percentile_choices: string[];
}

export interface AcceptRejectSellAssetRequestPayload {
  groupId: string;
  serviceId: string;
  unitType: 'incomeUnits' | 'growthUnits';
  assetSellId: string;
  action: 'accepted' | 'rejected';
  date_str: string;
}

export interface AcceptRejectSellAssetRequestResponse {
  success: boolean;
  message: string;
}


export interface Asset {
  asset_id: string;
  asset_name: string;
  asset_type: 'stock' | 'property' | 'bond';
  initial_investment: {
    purchase_price: number;
    upfront_costs: {
      [key: string]: number;
    };
  };
  financing?: {
    loan_amount?: number;
    interest_rate: {
      type: string;
      min: number;
      max: number;
    };
    term_years?: number;
    payment_frequency?: string;
  };
  income_streams?: {
    primary_income: {
      amount: number;
      frequency: 'annual' | 'monthly';
      growth_rate: {
        type: string;
        min: number;
        max: number;
      };
    };
  };
  expenses: {
    fixed_annual?: {
      [key: string]: number;
    };
    variable_percent: {
      [key: string]: number;
    };
  };
  market_assumptions: {
    capital_growth?: {
      type?: string;
      min?: number;
      max?: number;
      fixed_value?: number;
    };
    volatility: {
      annual_std_deviation: number;
    };
    market_cycles: {
      recession_probability: number;
      recession_impact: number;
    };
  };
  risk_factors: {
    [key: string]: number;
  };
}

export interface SimulationPayload {
  simulation_config: SimulationConfig;
  assets: Asset[];
}

// Frequency interface for automation
export interface AutomationFrequency {
  daily: boolean;
  weekly: boolean;
  fortnightly: boolean;
  monthly: boolean;
  quarterly: boolean;
  'bi-annually': boolean;
  yearly: boolean;
}

// Updated ongoing contribution interface based on API response
export interface OngoingContribution {
  groupId: string;
  automationId?: string;
  serviceId: string;
  unitType: 'incomeUnits' | 'growthUnits';
  isEnableAutomate: boolean;
  frequency: AutomationFrequency;
  contributionAmount: number;
  createdBy?: string;
  createdDate?: string;
  updatedBy?: string;
  updatedDate?: string;
}

// Updated payload interface to include both get and update actions
export type OngoingContributionPayload =
  | {
      action: 'get';
      groupId: string;
      serviceId: string;
      unitType: 'incomeUnits' | 'growthUnits';
    }
  | {
      action: 'update';
      groupId: string;
      serviceId: string;
      unitType: 'incomeUnits' | 'growthUnits';
      isEnableAutomate: boolean;
      frequency: AutomationFrequency;
      contributionAmount: number;
      automationId?: string;
    };

// Response interface for get action - handle empty data case
export interface OngoingContributionGetResponse {
  data: OngoingContribution | {}; // Can be empty object
  message?: string;
}

// Response interface for update action
export interface OngoingContributionUpdateResponse {
  success: boolean;
  message: string;
  data: OngoingContribution;
}

// Manual trigger payload interface
export interface ManualTriggerPayload {
  groupId: string;
  serviceId: string;
  unitType: 'incomeUnits' | 'growthUnits';
}

// Manual trigger response interface
export interface ManualTriggerResponse {
  success: boolean;
  message: string;
}

// Expenses manual trigger payload interface
export interface ExpensesManualTriggerPayload {
  groupId: string;
  serviceId: string;
  unitType: 'incomeUnits' | 'growthUnits';
}

// Expenses manual trigger response interface
export interface ExpensesManualTriggerResponse {
  success: boolean;
  message: string;
}

// Unified Accept/Reject request payload interface - supports both single and batch operations
export interface AcceptRejectRequestPayload {
  groupId: string;
  serviceId: string;
  unitType: 'incomeUnits' | 'growthUnits';
  requests: Array<{
    frequency: string;
    date: string;
    requestId: string;
    action: 'accepted' | 'rejected';
  }>;
}

// Unified Accept/Reject request response interface
export interface AcceptRejectRequestResponse {
  success: boolean;
  message: string;
  results?: Array<{
    requestId: string;
    success: boolean;
    message?: string;
  }>;
}

// Sell Asset Payload Interface
export interface SellAssetPayload {
  groupId: string;
  serviceId: string;
  unitType: 'incomeUnits' | 'growthUnits';
  assetId: string;
  sellValue: number;
  deductions: number;
  finalPrice: number;
}

// Sell Asset Response Interface
export interface SellAssetResponse {
  status: string;
  message: string;
  assetSellId: string;
}

// Sell Asset Request Item Interface
export interface SellAssetRequestItem {
  assetSellId: string;
  assetId: string;
  unitType: 'incomeUnits' | 'growthUnits';
  sellValue: string;
  deductions: string;
  finalPrice: string;
  status: string;
  createdAt: string;
  lastUpdatedDateTime: string;
  lastUpdatedBy: string;
  requestDate: string;
}

// Get Sell Asset Requests Payload Interface
export interface GetSellAssetRequestsPayload {
  groupId: string;
  serviceId: string;
  unitType: 'incomeUnits' | 'growthUnits';
  pageSize?: number;
  currentPage?: number;
  nextToken?: string | null;
  fromDate?: string;
  toDate?: string;
  status?: string;
  assetId?: string;
}

// Get Sell Asset Requests Response Interface
export interface GetSellAssetRequestsResponse {
  sellAssetRequests: SellAssetRequestItem[];
  totalCount: number;
  pageSize: number;
  currentPage: number;
  totalPages: number;
  nextToken: string | null;
  hasMore: boolean;
  filters?: {
    fromDate?: string;
    toDate?: string;
    status?: string;
    assetId?: string;
  };
}

// Add these interfaces after the existing interfaces

// Certificate Interface
export interface Certificate {
  certificateId: string;
  transactionType: 'buy' | 'sell';
  units: string;
  unitPrice: string;
  investorName: string;
  investorEmail: string;
  investorAvatar: string;
  transactionDate: string;
}

// Get Current Buy Sell Certificate Payload Interface
export interface GetCurrentBuySellCertificatePayload {
  groupId: string;
  serviceId: string;
  unitType?: 'incomeUnits' | 'growthUnits';
  investorEmail: string;
}

// Get Current Buy Sell Certificate Response Interface
export interface GetCurrentBuySellCertificateResponse {
  status: string;
  data: {
    buyCertificate: Certificate;
    sellCertificate: Certificate;
  };
}

// Response interface remains the same for simulation
export interface SimulationResult {
  simulation_id: string;
  simulation_date: string;
  simulation_config: SimulationConfig;
  individual_assets: Array<{
    asset_summary: {
      asset_id: string;
      asset_name: string;
      asset_type: string;
      initial_investment: number;
      iterations_completed: number;
      investment_period_years: number;
    };
    investment_performance_analysis: {
      expected_returns_p50: {
        final_value: string;
        total_profit: string;
        annual_return: string;
        break_even: string;
      };
      risk_assessment: {
        probability_of_profit: string;
        probability_of_loss: string;
        worst_case: string;
        best_case: string;
        confidence_interval_80: {
          min_return: string;
          max_return: string;
        };
      };
      year_by_year_insights: {
        early_years: {
          description: string;
          key_characteristics: string[];
        };
        middle_period: {
          description: string;
          key_characteristics: string[];
        };
        later_years: {
          description: string;
          key_characteristics: string[];
        };
      };
      risk_distribution_analysis: {
        histogram_insights: string[];
        outcome_clustering: {
          lower_quartile: string;
          median: string;
          upper_quartile: string;
          interquartile_range: string;
        };
        volatility_analysis: {
          early_years_volatility: string;
          later_years_volatility: string;
          volatility_trend: string;
        };
      };
      strategic_decision_points: {
        time_horizon_matters: {
          short_term_risk: string;
          long_term_reward: string;
          patience_benefit: string;
        };
        risk_vs_reward_profile: {
          conservative_estimate: string;
          realistic_expectation: string;
          optimistic_scenario: string;
        };
        market_timing_insights: string[];
      };
      comparison_benchmarks: {
        against_alternatives: {
          term_deposits: string;
          bonds: string;
          reit: string;
        };
        risk_adjusted_assessment: string;
      };
      actionable_investment_decisions: {
        green_lights: string[];
        red_flags_to_monitor: string[];
      };
      portfolio_strategy_implications: {
        optimal_investor_profile: string[];
        position_sizing_recommendation: string;
      };
      key_takeaway: string;
    };
    final_outcomes: {
      total_asset_value: { P10: number; P50: number; P90: number };
      total_net_profit: { P10: number; P50: number; P90: number };
      annual_irr_percent: { P10: number; P50: number; P90: number };
      roi_percent: { P10: number; P50: number; P90: number };
    };
    yearly_projections: Array<{
      year: number;
      asset_value: { P10: number; P50: number; P90: number };
      annual_income: { P10: number; P50: number; P90: number };
      annual_expenses: { P10: number; P50: number; P90: number };
      net_cashflow: { P10: number; P50: number; P90: number };
      cumulative_profit: { P10: number; P50: number; P90: number };
    }>;
    distribution_data_for_charts: {
      histogram_final_returns: {
        bins: number[];
        frequencies: number[];
      };
      asset_value_time_series: {
        years: number[];
        P10_values: number[];
        P50_values: number[];
        P90_values: number[];
      };
      cashflow_distribution: {
        years: number[];
        P10_cashflows: number[];
        P50_cashflows: number[];
        P90_cashflows: number[];
      };
      risk_metrics: {
        value_at_risk_95: number;
        expected_shortfall: number;
        standard_deviation: number;
        skewness: number;
        kurtosis: number;
      };
    };
    simulation_id: string;
  }>;
  portfolio_summary: any;
}

// Accept/Reject Sell Units Request Payload Interface
export interface AcceptRejectSellUnitsPayload {
  groupId: string;
  serviceId: string;
  unitType: 'incomeUnits' | 'growthUnits';
  requestId: string;
  date_str: string;
  action: 'accepted' | 'rejected';
}

// Accept/Reject Sell Units Request Response Interface
export interface AcceptRejectSellUnitsResponse {
  success: boolean;
  message: string;
  requestId: string;
}

// Buy Selling Unit Request Item Interface
export interface BuySellingUnitRequestItem {
  requestId: string;
  unitType: 'incomeUnits' | 'growthUnits';
  sellerEmailId: string;
  sellerName: string;
  sellerAvatar: string;
  sellUnits: string;
  unitPrice: string;
  operationCharge: string;
  sellerWillGetAmount: string;
  date_str: string;
  isBuyRequestSomeOne: boolean;
  status: string;
  createdAt: string;
  lastUpdatedDateTime: string;
  lastUpdatedBy: string;
  requestDate: string;
}

// Buy Selling Units Dashboard List Payload Interface
export interface BuySellingUnitsDashboardPayload {
  groupId: string;
  serviceId: string;
  unitType: 'incomeUnits' | 'growthUnits';
  pageSize?: number;
  currentPage?: number;
  nextToken?: string | null;
  fromDate?: string;
  toDate?: string;
  status?: string;
}

// Buy Selling Units Dashboard List Response Interface
export interface BuySellingUnitsDashboardResponse {
  buySellingUnitRequests: BuySellingUnitRequestItem[];
  totalCount: number;
  pageSize: number;
  currentPage: number;
  totalPages: number;
  nextToken: string | null;
  hasMore: boolean;
  filters?: {
    fromDate?: string;
    toDate?: string;
    status?: string;
  };
}

export interface SimulationState {
  loading: boolean;
  error: string | null;
  result: SimulationResult | null;
  contributionLoading: boolean;
  contributionError: string | null;
  incomeUnitsConfig: OngoingContribution | null;
  growthUnitsConfig: OngoingContribution | null;
  manualTriggerLoading: boolean;
  manualTriggerError: string | null;
  manualTriggerResponse: ManualTriggerResponse | null;
  expensesManualTriggerLoading: boolean;
  expensesManualTriggerError: string | null;
  expensesManualTriggerResponse: ExpensesManualTriggerResponse | null;
  acceptRejectLoading: boolean;
  acceptRejectError: string | null;
  acceptRejectResponse: AcceptRejectRequestResponse | null;
  sellAssetLoading: boolean;
  sellAssetError: string | null;
  sellAssetResponse: SellAssetResponse | null;
  sellAssetRequestsLoading: boolean;
  sellAssetRequestsError: string | null;
  sellAssetRequestsData: GetSellAssetRequestsResponse | null;
  acceptRejectSellUnitsLoading: boolean;
  acceptRejectSellUnitsError: string | null;
  acceptRejectSellUnitsResponse: AcceptRejectSellUnitsResponse | null;
  buySellingUnitsDashboardLoading: boolean;
  buySellingUnitsDashboardError: string | null;
  buySellingUnitsDashboardData: BuySellingUnitsDashboardResponse | null;
  // NEW: State for get current buy sell certificate
  currentBuySellCertificateLoading: boolean;
  currentBuySellCertificateError: string | null;
  currentBuySellCertificateData: GetCurrentBuySellCertificateResponse | null;
  acceptRejectSellAssetLoading: boolean;
  acceptRejectSellAssetError: string | null;
  acceptRejectSellAssetResponse: AcceptRejectSellAssetRequestResponse | null;
}

// Existing simulation async thunk
export const runSimulation = createAsyncThunk(
  'simulation/run',
  async (payload: SimulationPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/fund-investment-management/simulation', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Simulation failed');
    }
  }
);

// Helper function to check if data object has valid automation config
const isValidAutomationConfig = (data: any): data is OngoingContribution => {
  return (
    data &&
    typeof data === 'object' &&
    typeof data.groupId === 'string' &&
    typeof data.serviceId === 'string' &&
    typeof data.unitType === 'string' &&
    typeof data.isEnableAutomate === 'boolean' &&
    typeof data.frequency === 'object' &&
    typeof data.contributionAmount === 'number' &&
    !isNaN(data.contributionAmount)
  );
};


export const acceptRejectSellAssetRequest = createAsyncThunk(
  'simulation/acceptRejectSellAssetRequest',
  async (payload: AcceptRejectSellAssetRequestPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/fund-investment-management/accept-reject-sell-asset-request',
        payload
      );
      return response.data as AcceptRejectSellAssetRequestResponse;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to process sell asset request'
      );
    }
  }
);


// NEW: Async thunk for get current buy sell certificate
export const getCurrentBuySellCertificate = createAsyncThunk(
  'simulation/getCurrentBuySellCertificate',
  async (payload: GetCurrentBuySellCertificatePayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/fund-investment-management/get-current-buy-sell-certificate',
        payload
      );
      return response.data as GetCurrentBuySellCertificateResponse;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch current buy sell certificate'
      );
    }
  }
);

// Updated async thunk for ongoing contribution setup (supports both get and update actions)
export const manageOngoingContribution = createAsyncThunk(
  'simulation/manageOngoingContribution',
  async (payload: OngoingContributionPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/fund-investment-management/ongoing-contribution-set-up',
        payload
      );
      return {
        data: response.data,
        action: payload.action,
        unitType: payload.unitType
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Contribution operation failed');
    }
  }
);

// Async thunk for manual trigger
export const triggerManualContribution = createAsyncThunk(
  'simulation/triggerManualContribution',
  async (payload: ManualTriggerPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/fund-investment-management/contribution-mannual-trigger',
        payload
      );
      return response.data as ManualTriggerResponse;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Manual contribution trigger failed');
    }
  }
);

// Async thunk for expenses manual trigger
export const triggerExpensesManual = createAsyncThunk(
  'simulation/triggerExpensesManual',
  async (payload: ExpensesManualTriggerPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/fund-investment-management/expenses-mannual-trigger',
        payload
      );
      return response.data as ExpensesManualTriggerResponse;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Expenses manual trigger failed');
    }
  }
);

// Unified async thunk for accept/reject ongoing contribution request (handles both single and batch)
export const acceptRejectContributionRequest = createAsyncThunk(
  'simulation/acceptRejectContributionRequest',
  async (payload: AcceptRejectRequestPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/fund-investment-management/accept-reject-ongoing-contribution-request',
        payload
      );
      return response.data as AcceptRejectRequestResponse;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to process contribution request'
      );
    }
  }
);

// Async thunk for sell asset
export const sellAsset = createAsyncThunk(
  'simulation/sellAsset',
  async (payload: SellAssetPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/fund-investment-management/sell-asset', payload);
      return response.data as SellAssetResponse;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to create sell asset request'
      );
    }
  }
);

// Async thunk for get sell asset requests
export const getSellAssetRequests = createAsyncThunk(
  'simulation/getSellAssetRequests',
  async (payload: GetSellAssetRequestsPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/fund-investment-management/sell-asset-requests',
        payload
      );
      return response.data as GetSellAssetRequestsResponse;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch sell asset requests'
      );
    }
  }
);

// NEW: Async thunk for accept/reject sell units request
export const acceptRejectSellUnitsRequest = createAsyncThunk(
  'simulation/acceptRejectSellUnitsRequest',
  async (payload: AcceptRejectSellUnitsPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/fund-investment-management/accept-reject-sell-units-request',
        payload
      );
      return response.data as AcceptRejectSellUnitsResponse;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to process sell units request'
      );
    }
  }
);

// NEW: Async thunk for buy selling units dashboard list
export const getBuySellingUnitsDashboard = createAsyncThunk(
  'simulation/getBuySellingUnitsDashboard',
  async (payload: BuySellingUnitsDashboardPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/fund-investment-management/buy-selling-units-dashboard-list',
        payload
      );
      return response.data as BuySellingUnitsDashboardResponse;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch buy selling units dashboard data'
      );
    }
  }
);

// Update the initialState

const initialState: SimulationState = {
  loading: false,
  error: null,
  result: null,
  contributionLoading: false,
  contributionError: null,
  incomeUnitsConfig: null,
  growthUnitsConfig: null,
  manualTriggerLoading: false,
  manualTriggerError: null,
  manualTriggerResponse: null,
  expensesManualTriggerLoading: false,
  expensesManualTriggerError: null,
  expensesManualTriggerResponse: null,
  acceptRejectLoading: false,
  acceptRejectError: null,
  acceptRejectResponse: null,
  sellAssetLoading: false,
  sellAssetError: null,
  sellAssetResponse: null,
  sellAssetRequestsLoading: false,
  sellAssetRequestsError: null,
  sellAssetRequestsData: null,
  acceptRejectSellUnitsLoading: false,
  acceptRejectSellUnitsError: null,
  acceptRejectSellUnitsResponse: null,
  buySellingUnitsDashboardLoading: false,
  buySellingUnitsDashboardError: null,
  buySellingUnitsDashboardData: null,
  // NEW
  currentBuySellCertificateLoading: false,
  currentBuySellCertificateError: null,
  currentBuySellCertificateData: null,
  acceptRejectSellAssetLoading: false,
  acceptRejectSellAssetError: null,
  acceptRejectSellAssetResponse: null
};

const simulationSlice = createSlice({
  name: 'simulation',
  initialState,
  reducers: {
    clearSimulationResult: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    },
    clearContributionData: (state) => {
      state.incomeUnitsConfig = null;
      state.growthUnitsConfig = null;
      state.contributionError = null;
      state.contributionLoading = false;
    },
    clearManualTriggerResponse: (state) => {
      state.manualTriggerResponse = null;
      state.manualTriggerError = null;
      state.manualTriggerLoading = false;
    },
    clearExpensesManualTriggerResponse: (state) => {
      state.expensesManualTriggerResponse = null;
      state.expensesManualTriggerError = null;
      state.expensesManualTriggerLoading = false;
    },
    clearAcceptRejectResponse: (state) => {
      state.acceptRejectResponse = null;
      state.acceptRejectError = null;
      state.acceptRejectLoading = false;
    },
    clearSellAssetResponse: (state) => {
      state.sellAssetResponse = null;
      state.sellAssetError = null;
      state.sellAssetLoading = false;
    },
    clearSellAssetRequestsData: (state) => {
      state.sellAssetRequestsData = null;
      state.sellAssetRequestsError = null;
      state.sellAssetRequestsLoading = false;
    },
    // NEW
    clearAcceptRejectSellUnitsResponse: (state) => {
      state.acceptRejectSellUnitsResponse = null;
      state.acceptRejectSellUnitsError = null;
      state.acceptRejectSellUnitsLoading = false;
    },
    clearBuySellingUnitsDashboardData: (state) => {
      state.buySellingUnitsDashboardData = null;
      state.buySellingUnitsDashboardError = null;
      state.buySellingUnitsDashboardLoading = false;
    },
    setAutomationConfig: (state, action: PayloadAction<OngoingContribution>) => {
      if (action.payload.unitType === 'incomeUnits') {
        state.incomeUnitsConfig = action.payload;
      } else {
        state.growthUnitsConfig = action.payload;
      }
    },
    clearCurrentBuySellCertificateData: (state) => {
      state.currentBuySellCertificateData = null;
      state.currentBuySellCertificateError = null;
      state.currentBuySellCertificateLoading = false;
    },
    clearAcceptRejectSellAssetResponse: (state) => {
      state.acceptRejectSellAssetResponse = null;
      state.acceptRejectSellAssetError = null;
      state.acceptRejectSellAssetLoading = false;
    }
  },
  extraReducers: (builder) => {
    // Existing simulation reducers
    builder
      .addCase(runSimulation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(runSimulation.fulfilled, (state, action: PayloadAction<SimulationResult>) => {
        state.loading = false;
        state.result = action.payload;
      })
      .addCase(runSimulation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Ongoing contribution management (supports both get and update actions)
      .addCase(manageOngoingContribution.pending, (state) => {
        state.contributionLoading = true;
        state.contributionError = null;
      })
      .addCase(manageOngoingContribution.fulfilled, (state, action) => {
        state.contributionLoading = false;
        const { data, action: operationType, unitType } = action.payload;
        switch (operationType) {
          case 'get': {
            const getData = data as OngoingContributionGetResponse;
            if (isValidAutomationConfig(getData.data)) {
              if (unitType === 'incomeUnits') {
                state.incomeUnitsConfig = getData.data as OngoingContribution;
              } else {
                state.growthUnitsConfig = getData.data as OngoingContribution;
              }
            } else {
              if (unitType === 'incomeUnits') {
                state.incomeUnitsConfig = null;
              } else {
                state.growthUnitsConfig = null;
              }
            }
            break;
          }
          case 'update': {
            const updateData = data as OngoingContributionUpdateResponse;
            if (updateData.success && updateData.data && isValidAutomationConfig(updateData.data)) {
              if (unitType === 'incomeUnits') {
                state.incomeUnitsConfig = updateData.data;
              } else {
                state.growthUnitsConfig = updateData.data;
              }
            }
            break;
          }
        }
      })
      .addCase(manageOngoingContribution.rejected, (state, action) => {
        state.contributionLoading = false;
        state.contributionError = action.payload as string;
      })
      // Manual trigger reducers
      .addCase(triggerManualContribution.pending, (state) => {
        state.manualTriggerLoading = true;
        state.manualTriggerError = null;
        state.manualTriggerResponse = null;
      })
      .addCase(triggerManualContribution.fulfilled, (state, action) => {
        state.manualTriggerLoading = false;
        state.manualTriggerResponse = action.payload;
      })
      .addCase(triggerManualContribution.rejected, (state, action) => {
        state.manualTriggerLoading = false;
        state.manualTriggerError = action.payload as string;
      })
      // Expenses manual trigger reducers
      .addCase(triggerExpensesManual.pending, (state) => {
        state.expensesManualTriggerLoading = true;
        state.expensesManualTriggerError = null;
        state.expensesManualTriggerResponse = null;
      })
      .addCase(triggerExpensesManual.fulfilled, (state, action) => {
        state.expensesManualTriggerLoading = false;
        state.expensesManualTriggerResponse = action.payload;
      })
      .addCase(triggerExpensesManual.rejected, (state, action) => {
        state.expensesManualTriggerLoading = false;
        state.expensesManualTriggerError = action.payload as string;
      })
      // Unified Accept/Reject request reducers
      .addCase(acceptRejectContributionRequest.pending, (state) => {
        state.acceptRejectLoading = true;
        state.acceptRejectError = null;
        state.acceptRejectResponse = null;
      })
      .addCase(acceptRejectContributionRequest.fulfilled, (state, action) => {
        state.acceptRejectLoading = false;
        state.acceptRejectResponse = action.payload;
      })
      .addCase(acceptRejectContributionRequest.rejected, (state, action) => {
        state.acceptRejectLoading = false;
        state.acceptRejectError = action.payload as string;
      })
      // Sell Asset reducers
      .addCase(sellAsset.pending, (state) => {
        state.sellAssetLoading = true;
        state.sellAssetError = null;
        state.sellAssetResponse = null;
      })
      .addCase(sellAsset.fulfilled, (state, action) => {
        state.sellAssetLoading = false;
        state.sellAssetResponse = action.payload;
      })
      .addCase(sellAsset.rejected, (state, action) => {
        state.sellAssetLoading = false;
        state.sellAssetError = action.payload as string;
      })
      // Get Sell Asset Requests reducers
      .addCase(getSellAssetRequests.pending, (state) => {
        state.sellAssetRequestsLoading = true;
        state.sellAssetRequestsError = null;
      })
      .addCase(getSellAssetRequests.fulfilled, (state, action) => {
        state.sellAssetRequestsLoading = false;
        state.sellAssetRequestsData = action.payload;
      })
      .addCase(getSellAssetRequests.rejected, (state, action) => {
        state.sellAssetRequestsLoading = false;
        state.sellAssetRequestsError = action.payload as string;
      })
      // NEW: Accept/Reject Sell Units Request reducers
      .addCase(acceptRejectSellUnitsRequest.pending, (state) => {
        state.acceptRejectSellUnitsLoading = true;
        state.acceptRejectSellUnitsError = null;
        state.acceptRejectSellUnitsResponse = null;
      })
      .addCase(acceptRejectSellUnitsRequest.fulfilled, (state, action) => {
        state.acceptRejectSellUnitsLoading = false;
        state.acceptRejectSellUnitsResponse = action.payload;
      })
      .addCase(acceptRejectSellUnitsRequest.rejected, (state, action) => {
        state.acceptRejectSellUnitsLoading = false;
        state.acceptRejectSellUnitsError = action.payload as string;
      })
      // NEW: Buy Selling Units Dashboard List reducers
      .addCase(getBuySellingUnitsDashboard.pending, (state) => {
        state.buySellingUnitsDashboardLoading = true;
        state.buySellingUnitsDashboardError = null;
      })
      .addCase(getBuySellingUnitsDashboard.fulfilled, (state, action) => {
        state.buySellingUnitsDashboardLoading = false;
        state.buySellingUnitsDashboardData = action.payload;
      })
      .addCase(getBuySellingUnitsDashboard.rejected, (state, action) => {
        state.buySellingUnitsDashboardLoading = false;
        state.buySellingUnitsDashboardError = action.payload as string;
      })

      // NEW: Get Current Buy Sell Certificate reducers
      .addCase(getCurrentBuySellCertificate.pending, (state) => {
        state.currentBuySellCertificateLoading = true;
        state.currentBuySellCertificateError = null;
      })
      .addCase(getCurrentBuySellCertificate.fulfilled, (state, action) => {
        state.currentBuySellCertificateLoading = false;
        state.currentBuySellCertificateData = action.payload;
      })
      .addCase(getCurrentBuySellCertificate.rejected, (state, action) => {
        state.currentBuySellCertificateLoading = false;
        state.currentBuySellCertificateError = action.payload as string;
      })
      //
      .addCase(acceptRejectSellAssetRequest.pending, (state: any) => {
        state.acceptRejectSellAssetLoading = true;
        state.acceptRejectSellAssetError = null;
        state.acceptRejectSellAssetResponse = null;
      })
      .addCase(
        acceptRejectSellAssetRequest.fulfilled,
        (state: any, action: PayloadAction<AcceptRejectSellAssetRequestResponse>) => {
          state.acceptRejectSellAssetLoading = false;
          state.acceptRejectSellAssetResponse = action.payload;
        }
      )
      .addCase(acceptRejectSellAssetRequest.rejected, (state: any, action: any) => {
        state.acceptRejectSellAssetLoading = false;
        state.acceptRejectSellAssetError = action.payload as string;
      });
  }
});

export const {
  clearSimulationResult,
  clearContributionData,
  clearManualTriggerResponse,
  clearExpensesManualTriggerResponse,
  clearAcceptRejectResponse,
  clearSellAssetResponse,
  clearSellAssetRequestsData,
  clearAcceptRejectSellUnitsResponse,
  clearBuySellingUnitsDashboardData,
  clearCurrentBuySellCertificateData, // NEW
  setAutomationConfig,
  clearAcceptRejectSellAssetResponse
} = simulationSlice.actions;

export default simulationSlice.reducer;
